import { CustomClaimValue, Device, FIRMWARE_PACKAGE_NAME, FRESH_INSTALL_VERSION, INSTALLABLE_CORE_PACKAGES, PackageVersion, Versions } from "@app/shared";
import { useMemo, useState } from "react";
import { toastSuccess } from "../../helpers";
import { useFetch } from "../../hooks";
import { AuthorizeView } from "./AuthorizeView";
import { SortableTable, SortableTableHeaderType } from "./SortableTable";


type DeviceUpgradeProps = {
    device: Device | undefined;
    onChange?: (device: Device) => void;
};

/**
 * Per-device package picker. Which packages count as "core" and which are offered before a
 * device has ever reported them lives in @app/shared's packageUpgrades helper, shared with the
 * batch action and the Fleet Commander console.
 */
export const DeviceUpgradeView: React.FC<DeviceUpgradeProps> = ({ device, onChange }) => {
    const [version, , versionHelper] = useFetch(() => device ? Versions.latestByDeviceId(device?.id) : Promise.resolve(undefined), [device]);
    const [selected, setSelected] = useState<Record<string, boolean>>({});
    const [showAdditional, setShowAdditional] = useState(false);
    const toggle = (id: string) => {
        setSelected(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const selectedCount = useMemo(
        () => Object.values(selected).filter(Boolean).length,
        [selected]
    );

    // The server refuses a second command while one is still outstanding, so say so before the
    // click rather than after it — the same guard the Fleet Commander console applies. Only the
    // button is blocked: what would change is still worth reading.
    const blocked = !!device?.hasPendingDeviceCommands;

    const handleUpgradeOnClick = async (device: Device | undefined) => {
        if (!device) return;
        const selectedKeys = Object.entries(selected)
            .filter(([_, value]) => value)
            .map(([key]) => key);
        await Versions.updateDevice(device.id, selectedKeys);
        if (onChange) await onChange(device);
        toastSuccess('Upgrade Request Sent', `A request to upgrade the selected packages has been sent for device ${device.name}.`);
        setSelected({});

    };

    if (versionHelper.isLoading) return <div>Attempting to load version data...</div>;

    // A device that has never reported (or reported nothing) can still be queued an upgrade: the
    // command waits for its next check-in, and the firmware runs `apt update` before `apt install`,
    // so it picks up whatever is newest then. Without a report only the firmware package is offered —
    // the hardware-specific ones (sensor drivers, ISP tuning) can't be guessed.
    const packages = version?.packageVersions ?? [];
    const hasReport = packages.length > 0;
    const unreportedCorePackages = (hasReport ? INSTALLABLE_CORE_PACKAGES : [FIRMWARE_PACKAGE_NAME, ...INSTALLABLE_CORE_PACKAGES])
        .filter(name => !packages.some(pkg => pkg.name === name))
        .map(name => Object.assign(new PackageVersion(), {
            name,
            currentVersion: name === FIRMWARE_PACKAGE_NAME ? "unknown" : "not installed",
            upgradableTo: FRESH_INSTALL_VERSION,
        }));
    const omnisightPackages = packages.filter(pkg => pkg.name?.startsWith('os-')).concat(unreportedCorePackages);
    const additionalPackages = packages.filter(pkg => !pkg.name?.startsWith('os-') && pkg.upgradableTo !== undefined);

    const columns: SortableTableHeaderType<PackageVersion>[] = [
        {
            header: "",
            renderFunc: (pkg) => (
                <input
                    type="checkbox"
                    checked={!!selected[pkg?.name!]}
                    onChange={() => toggle(pkg?.name!)}
                />),
            width: 30
        },
        {
            header: "Name",
            dataKey: "name",
            width: 250
        },
        {
            header: "Version",
            dataKey: "currentVersion",
            width: 250
        },
        {
            header: "Available Version",
            dataKey: "upgradableTo",
            // No upgradableTo means the device's last report showed nothing newer — which can be out of
            // date if a build shipped since. Selecting it still works: apt installs the newest version.
            renderFunc: (pkg) => pkg?.upgradableTo
                ? <div className="text-green-600">{pkg.upgradableTo}</div>
                : <div className="text-muted-body-text" title="Nothing newer as of the device's last report. Select it to install the latest version when the device next checks in.">none reported</div>,
            width: 250
        },

    ];

    return (
        <>
            <div className="flex w-full">
                <h2 className="w-1/2 text-xl font-semibold mb-4">Core Packages</h2>
                <div className="m-5 w-1/2 text-right">
                    {version?.dateReported
                        ? <>Device report as of {version.dateReported.toDateString()} {version.dateReported.toLocaleTimeString()}</>
                        : <span className="text-red-500">This device has not reported its installed packages.</span>}
                </div>
            </div>
            <div className="mx-5 text-sm text-muted-body-text">
                Any package can be selected, even one with no available version reported. The upgrade is queued
                and runs when the device next checks in; it refreshes its package list first, so it installs
                the newest version published by then.
            </div>

            <div className="flex m-5 w-full">
                <SortableTable
                    initialSortDir="asc"
                    initialSortKey="name"
                    columns={columns}
                    data={omnisightPackages}
                    emptyValue={
                        <div className="my-5">
                            No upgrades found for this device
                        </div>
                    }
                />
            </div>
            <div className="flex m-5 w-full">
                <button
                    className="text-blue-600 hover:underline"
                    onClick={() => setShowAdditional(!showAdditional)}
                >
                    {showAdditional ? 'Hide' : 'Show'} Additional Packages
                </button>
            </div>

            <AuthorizeView claim={CustomClaimValue.SuperAdmin}>
                <div className="flex m-5 w-full flex-col">
                    {showAdditional && (<>
                        <h2 className="flex">Additional Packages</h2>

                        <div className="flex m-5">
                            <SortableTable
                                initialSortDir="asc"
                                initialSortKey="name"
                                columns={columns}
                                data={additionalPackages}
                                emptyValue={
                                    <div className="my-5">
                                        No upgrades found for this device
                                    </div>
                                }
                            />
                        </div>
                    </>)}

                </div>
            </AuthorizeView>


            {selectedCount > 0 && (
                <div className="sticky bottom-0 left-0 right-0 bg-white border-t shadow-md p-4 flex justify-between items-center z-50">
                    <span className="text-sm font-medium">
                        {blocked
                            ? "This device already has a command waiting — try again once it finishes."
                            : <>{selectedCount} package{selectedCount !== 1 && "s"} selected</>}
                    </span>
                    <button onClick={() => handleUpgradeOnClick(device)} disabled={blocked} className=" px-4 py-1 h-10 rounded-md block disabled:opacity-50 disabled:cursor-not-allowed bg-brand-primary active:bg-brand-primary-darkened text-bright-text text-text-brighthover:bg-brand-primary-lightened">
                        Upgrade Selected
                    </button>
                </div>
            )}
        </>
    );
}
